"use client";

import React from "react";
import { Tooltip } from "@/ui";
import { colors, shadows, spacing, radius, textStyles } from "@/ui/tokens";
import {
  Person,
  Work,
  Business,
  Dashboard,
  Analytics,
  Settings as SettingsIcon,
  Description,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import Image from "next/image";
import { useSOLAFloating } from "./context";

export function FloatingTrigger({
  onClick,
  hasNotifications = false,
  notificationCount = 0,
}) {
  const { state } = useSOLAFloating();

  const tooltipContent = (
    <div>
      <div style={{ fontWeight: 600, marginBottom: spacing[1] }}>SOLA AI Assistant</div>
      <div style={{ ...textStyles.caption, opacity: 0.8 }}>
        {state.isMinimized ? "Click to restore" : "Click to open"}
        {state.preferences.keyboardShortcuts && (
          <span style={{ marginLeft: spacing[2], fontFamily: "monospace" }}>
            (Ctrl+Shift+S)
          </span>
        )}
      </div>
      {state.pageContext.pageType !== "unknown" && (
        <div style={{ ...textStyles.caption, opacity: 0.7, marginTop: spacing[1] }}>
          Context: {state.pageContext.pageType}
          {state.pageContext.entityId && ` #${state.pageContext.entityId}`}
        </div>
      )}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} placement="top">
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          y: hasNotifications ? [0, -2, 0] : 0,
        }}
        transition={{
          y: {
            duration: 2,
            repeat: hasNotifications ? Infinity : 0,
            ease: "easeInOut",
          },
        }}
        style={{ position: "relative", display: "inline-flex" }}
      >
        {/* Notification badge */}
        {notificationCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 20,
              height: 20,
              borderRadius: radius.pill,
              backgroundColor: colors.signal.danger,
              color: colors.text.inverse,
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 5px",
              zIndex: 1,
              lineHeight: 1,
            }}
          >
            {notificationCount > 99 ? "99+" : notificationCount}
          </span>
        )}

        <button
          onClick={onClick}
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            border: "3px solid transparent",
            background:
              "linear-gradient(white, white) padding-box, linear-gradient(180deg, #9747FF 0%, #003299 43.75%, #0FDBCC 100%) border-box",
            boxShadow: shadows[4],
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.2s ease-in-out",
            padding: 0,
          }}
          aria-label="Open SOLA AI Assistant"
        >
          {/* Background pulse animation for notifications */}
          {hasNotifications && (
            <motion.div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          )}

          {/* Main icon */}
          <motion.div
            animate={{
              rotate: hasNotifications ? [0, 15, -15, 0] : 0,
            }}
            transition={{
              duration: 1,
              repeat: hasNotifications ? Infinity : 0,
              ease: "easeInOut",
            }}
          >
            <Image
              src="/img/floats/sola.svg"
              alt="SOLA"
              width={32}
              height={32}
              style={{
                display: "block",
              }}
            />
          </motion.div>
        </button>
      </motion.div>
    </Tooltip>
  );
}

// Quick action floating buttons (for when main window is open)
export function QuickActionButton({ action, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0, y: 20 }}
      transition={{
        delay: index * 0.1,
        type: "spring",
        stiffness: 500,
        damping: 30,
      }}
      style={{
        marginBottom: spacing[2],
      }}
    >
      <Tooltip content={action.label} placement="left">
        <button
          onClick={action.onClick}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "none",
            backgroundColor: colors.action.product,
            color: colors.text.inverse,
            boxShadow: shadows[2],
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            transition: "all 0.2s ease-in-out",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.action.productHover;
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = colors.action.product;
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          {action.icon}
        </button>
      </Tooltip>
    </motion.div>
  );
}

// Context-aware status indicator
export function ContextStatusIndicator() {
  const { state } = useSOLAFloating();

  if (state.pageContext.pageType === "unknown") return null;

  const getStatusColor = () => {
    switch (state.pageContext.pageType) {
      case "candidate":
        return colors.signal.info;
      case "job":
        return colors.signal.success;
      case "company":
        return colors.signal.warning;
      case "dashboard":
        return colors.action.product;
      default:
        return colors.text.muted;
    }
  };

  const getStatusIcon = () => {
    const iconStyle = { fontSize: 16 };
    switch (state.pageContext.pageType) {
      case "candidate":
        return <Person style={iconStyle} />;
      case "job":
        return <Work style={iconStyle} />;
      case "company":
        return <Business style={iconStyle} />;
      case "dashboard":
        return <Dashboard style={iconStyle} />;
      case "reports":
        return <Analytics style={iconStyle} />;
      case "settings":
        return <SettingsIcon style={iconStyle} />;
      default:
        return <Description style={iconStyle} />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        position: "fixed",
        top: 20,
        right: 20,
        zIndex: 9997,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: spacing[2],
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          paddingTop: spacing[2],
          paddingBottom: spacing[2],
          borderRadius: radius.md,
          backgroundColor: colors.canvas.default,
          border: `1px solid ${colors.border.subtle}`,
          boxShadow: shadows[2],
          ...textStyles.caption,
          fontWeight: 500,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", color: getStatusColor() }}>{getStatusIcon()}</span>
        <span style={{ ...textStyles.caption, color: getStatusColor() }}>
          {state.pageContext.pageType}
          {state.pageContext.entityId && ` #${state.pageContext.entityId}`}
        </span>
      </div>
    </motion.div>
  );
}
